import {
  PLATFORM_ID_TO_FRIENDLY_NAME,
  RENTALSCAPE_PROD,
  VALUE_DIRECTION,
} from '@/constants';
import round from 'lodash/round';

/**
 * Converts a phone number to a hyphenated phone number
 * @param {string} phoneNumber a phone number without the country code and only consists of numbers
 * @returns the hyphenated phone number (123-456-7890)
 */
export function convertStringToPhoneNumber(phoneNumber) {
  if (!phoneNumber || phoneNumber?.includes('-')) return phoneNumber;

  try {
    const phoneRegex = /(\d{3})(\d{3})(\d{4})/g;
    const phoneArray = phoneRegex.exec(phoneNumber).slice(1);
    return `${phoneArray[0]}-${phoneArray[1]}-${phoneArray[2]}`;
  } catch {
    return phoneNumber;
  }
}

/**
 * Converts number to string with comma separating the thousands. E.g. 3000 -> 3,000
 * @param {number|string} num number to be converted to string with commas in the thousands
 *
 * @returns {String}
 */
export function numberWithCommas(num) {
  /** sometimes, num === null
   * sometimes, num is 0 or '0'
   * we need to tell the difference
   */
  if (!num && num !== 0) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Returns string representation of number given, followed by a percent sign
 *
 * @param {Number} num
 * @returns {String}
 */
export function numberToPercent(num) {
  // we should allow number like 0%
  if (!num && num !== 0) return '';
  return `${num}%`;
}

export function dateFormatter(date) {
  const dateEnum = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const dateObj = new Date(date); // Create date objects so that extraction methods can be used
  // build the date in format Month-Year ie Jan 19, Feb 19 etc...
  const formattedDate = `${dateEnum[dateObj.getUTCMonth()]} ${dateObj
    .getFullYear()
    .toString()}`;
  return formattedDate; // New Date String Array
}

/**
 * Returns string representation of number in currency format
 *
 * @param {Number} num
 * @returns {String}
 */
export function numberStartsWithDollarSign(num) {
  if (!num && num !== 0) return null;
  return `$${numberWithCommas(num)}`;
}

// TODO: rename this please. I think it's for the horizontal bar in side panel where on hover
// data is displayed as `12345 (10%)`
export function messageBuilder(value, percentage) {
  const message = `${numberWithCommas(value)} (${numberToPercent(percentage)})`;
  return message;
}

const now = new Date();
const thisYr = now.getFullYear();
const thisMo = now.getMonth();
export const getKeyForPrevious1Mo = () => {
  let key = '';
  if (thisMo === 0) {
    // this mo is Jan, so Dec
    key = `${thisYr - 1}-12`;
  } else {
    key = `${thisYr}-${thisMo >= 10 ? thisMo : `0${thisMo}`}`;
  }
  return key;
};

export const getKeyForPrevious2Mo = () => {
  let key = '';
  if (thisMo === 0) {
    // this mo is Jan, so Nov
    key = `${thisYr - 1}-11`;
  } else if (thisMo === 1) {
    // this mo is Feb, so, Dec
    key = `${thisYr - 1}-12`;
  } else {
    key = thisMo === 11 ? `${thisYr}-10` : `${thisYr}-0${thisMo - 1}`;
  }
  return key;
};

/**
 * Adds protocol + base of url if it does not have it already.
 * E.g... makes xxx.com become https://xxx.com
 *
 * @param {string} url
 */
// TODO: Danger! Images always point to prod.. Must use appConfig
export const generateFullUrl = (url) => {
  if (!url) return null;

  const isFullUrl = url.search('http');
  if (isFullUrl < 0) {
    return `${RENTALSCAPE_PROD}${url}`;
  }
  return url;
};

/**
 * Turns thousands and millions to K and M... Billions and beyond not abbreviated
 *
 * @param {Number} num
 * @returns {Number}
 */
export function numberToAbbreviatedNumber(num) {
  // If an input cannot be converted to a number
  if (!Number(num)) return null;

  if (num >= 1000000) return `${round(num / 1000000, 1)}M`;
  if (num >= 1000) return `${round(num / 1000, 1)}K`;
  return num;
}

/**
 *
 * @param {String} str
 */

const specialWords = ['LLC', 'LLP', 'L.L.C.', 'L.L.P.'];

export const toTitleCase = (str) =>
  str
    .split(' ')
    .map((word) => {
      if (specialWords.includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

export function extractSpreadsheetFilename(headers, regType) {
  const FILENAME_LOCATION = 1;
  const CONTENT_DISPOSITION = 'content-disposition';
  const filenameFromServer =
    headers[CONTENT_DISPOSITION].split('=')[FILENAME_LOCATION];
  const filename = `${regType}-${filenameFromServer}`;
  return filename;
}

// Ensures that the description column is always sorted ignoring case sensitivity
export function caseInsensitiveComparator(valueA, valueB) {
  // Convert null column values to empty strings
  const valueANonNull = valueA ?? '';
  const valueBNonNull = valueB ?? '';
  return valueANonNull.toLowerCase().localeCompare(valueBNonNull.toLowerCase());
}

/**
 * Formats a value with a supplied unit.
 *
 * @param {number} value
 * @param {'$' | '%'} unit
 * @param {boolean} abbreviate
 * @param {number} direction This is inferred from the value, however it can be forced if you want to display negative
 * formats from a postive value or vice-versa.
 * @returns {string}
 *
 * @example
 * formatUnit(123456, '$', false, VALUE_DIRECTION.DECREASE) -> '-$123,456'
 * formatUnit(123456, '$', false, VALUE_DIRECTION.INCREASE) -> '+$123,456'
 * formatUnit(123456, '$', false) -> '$123,456'
 * formatUnit(123456, '$', true, VALUE_DIRECTION.INCREASE) -> '+$123K'
 * formatUnit(123456, '$', true) -> '$123K'
 * formatUnit(123456) -> '123,456'
 */
export function formatUnit(
  value,
  unit = undefined,
  abbreviate = false,
  direction = undefined
) {
  if (!Number.isFinite(value)) return '';

  // If the direction is explicitly set then it will be used, otherwise this must infer the direction from the value.
  // normally positive values do not prepend a sign (ie. +) this is why the inference defaults to 0 and not >0.
  // if direction is set to a value >0 then we'll assume that the caller wants the positive sign displayed.
  direction =
    direction ?? (value < 0 ? VALUE_DIRECTION.DECREASE : VALUE_DIRECTION.NONE);

  const sign =
    direction === VALUE_DIRECTION.INCREASE
      ? '+'
      : direction === VALUE_DIRECTION.DECREASE
        ? '-'
        : '';
  const abs = Math.abs(value);
  const fv = numberWithCommas(
    abbreviate ? numberToAbbreviatedNumber(abs) : abs
  );

  switch (unit) {
    case '$':
      return `${sign}$${fv}`;
    case '%':
      return `${sign}${fv}%`;
    default:
      return `${sign}${fv}`;
  }
}

export const capitalize = (s) => s[0].toUpperCase() + s.slice(1);

export function getPlatformFriendlyName(platform, platformIconsConfig) {
  let friendlyName = platformIconsConfig?.[platform]?.name;
  if (friendlyName) {
    return friendlyName;
  }

  friendlyName = PLATFORM_ID_TO_FRIENDLY_NAME[platform];
  if (friendlyName) {
    return friendlyName;
  }

  return capitalize(platform);
}
